import React, { useEffect, useRef } from "react";
import classNames from "classnames";
import { DateProps } from "../types";
import ClearButton from "../ClearButton";
import MobileInputs from "./subComponents/MobileInputs";
import DesktopCalendar from "./subComponents/DesktopCalendar";
import FormField from "../FormField";
import { useDatePicker } from "./hooks/useDatePicker";

import "./styles.scss";

export const DatePicker = ({
  t,
  field,
  register,
  touchedFields,
  setValue,
  errors,
  clearErrors,
  watch,
  trigger,
  className: classNameProp,
  isTablet,
}: DateProps) => {
  const inputDataQA = `${field.type}-${field.name}`;
  const inputID = field.id || field.name;
  const inputRegister = register(field.name, field.validation);
  const rhfRef = inputRegister.ref;
  const datePickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const minDate = field.minElegibleDate;
  const maxDate = field.maxElegibleDate;
  const dateValue = watch(field.name);
  const dateTrip = watch("dateTrip");
  const dateReturn = watch("dateReturn");
  const launchValidation = () => trigger(field.name);
  const persistValue = (value: string) => setValue(field.name, value);
  const placeholderText = field.placeholder ? t[field.placeholder] : "MM/DD/YYYY";

  const {
    isOpen,
    setIsOpen,
    inputDate,
    inputError,
    month,
    setMonth,
    selectedDate,

    // mobile states
    inputMonth,
    inputDay,
    inputYear,
    updateMobileValues,

    //refs
    mobileRef,

    // handlers
    handleBlur,
    handleBlurMobile,
    handleChange,
    handleChangeMobile,
    handleDayPickerSelect,
    handleReset,

    // limits
    minDateAvailable,
    maxDateAvailable,
  } = useDatePicker({
    range: {
      minEligibleDate: minDate || "",
      maxEligibleDate: maxDate || "",
    },
    dateTrip,
    dateReturn,
    dateValue,
    launchValidation,
    persistValue,
    isTablet,
  });

  const className = classNames(`DatePickerWrapper`, {
    [classNameProp as string]: classNameProp,
  });
  const classNameInput = classNames(`input DateInput`, {
    [field.class as string]: typeof field.class === "string" && field.class.length > 0,
  });

  useEffect(() => {
    if (!isTablet) return;
    updateMobileValues(inputDate);
  }, [inputDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Use a timeout to defer blur() after React finishes re-rendering
        // (avoids DOM manipulation during render and potential re-render cascades)
        setTimeout(() => {
          inputRef.current?.blur();
        }, 0);
      }
    };
    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen]);

  const baseProps = {
    id: inputID,
    "data-qa": inputDataQA,
    "aria-label": t[field.name],
    className: "input__field",
  };

  const isFieldTouched = touchedFields[field.name];
  const fieldHasErrors = errors && errors[field.name];
  const validField = isFieldTouched && !fieldHasErrors;

  const errorMessage =
    t[inputError] || (fieldHasErrors && t[errors[field.name]?.message as string]) || undefined;

  return (
    <div
      ref={datePickerRef}
      className={className}
      onClick={() => setIsOpen(true)}>
      <FormField
        id={inputID}
        name={field.name}
        label={t[field.label]}
        className={classNameInput}
        helperMessage={
          (field.helperMessage && t[field.helperMessage]) || (field.help && t[field.help])
        }
        errorMessage={errorMessage}
        disclaimerAfterField={
          (field.disclaimer && t[field.disclaimer]) ||
          (field.disclaimerOutside && t[field.disclaimerOutside])
        }
        disclaimerAlert={field.disclaimerAlert && t[field.disclaimerAlert]}
        tooltip={field.tooltip && t[field.tooltip]}
        validate={!errorMessage}
        validField={validField}>
        {!isTablet && (
          <>
            <div className={"input__fieldWrapper"}>
              <input
                {...baseProps}
                {...inputRegister}
                ref={(el) => {
                  rhfRef(el);
                  inputRef.current = el;
                }}
                onBlur={handleBlur}
                className="input__field"
                placeholder={placeholderText}
                type="text"
                value={inputDate}
                onChange={handleChange}
              />
              {(dateValue || inputDate !== "") && (
                <ClearButton
                  handleClick={handleReset}
                  dataQa={`clear-date-${field.name}`}
                />
              )}
            </div>

            {isOpen && (
              <DesktopCalendar
                month={month}
                setMonth={setMonth}
                selectedDate={selectedDate}
                minDate={minDateAvailable}
                maxDate={maxDateAvailable}
                handleSelect={handleDayPickerSelect}
              />
            )}
          </>
        )}

        {isTablet && (
          <MobileInputs
            t={t}
            inputDay={inputDay}
            inputMonth={inputMonth}
            inputYear={inputYear}
            handleChange={handleChangeMobile}
            handleBlur={handleBlurMobile}
            mobileRef={mobileRef}
            baseProps={baseProps}
            rhfRef={rhfRef}
            hiddenValue={dateValue || ""}
          />
        )}
      </FormField>
    </div>
  );
};
